/**
 * Force load product card images with queue management
 * Ensures all product images load properly without overwhelming the browser
 */

// Queue manager to prevent overwhelming browser with too many simultaneous requests
class ImageLoadQueue {
  constructor(concurrency = 6) {
    this.concurrency = concurrency;
    this.queue = [];
    this.running = 0;
  }

  add(fn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ fn, resolve, reject });
      this.process();
    });
  }

  async process() {
    if (this.running >= this.concurrency || this.queue.length === 0) {
      return;
    }

    this.running++;
    const { fn, resolve, reject } = this.queue.shift();

    try {
      const result = await fn();
      resolve(result);
    } catch (error) {
      reject(error);
    } finally {
      this.running--;
      this.process();
    }
  }
}

const imageQueue = new ImageLoadQueue(6); // Max 6 concurrent image loads

function forceLoadProductImages() {
  const productCards = document.querySelectorAll('.product-card');

  productCards.forEach((card, cardIndex) => {
    const images = card.querySelectorAll('img');

    images.forEach((img, imgIndex) => {
      // Skip if already loaded
      if (img.complete && img.naturalWidth > 0) {
        img.classList.add('loaded');
        return;
      }

      // Set loading to eager
      img.setAttribute('loading', 'eager');
      img.removeAttribute('data-lazy');

      // Get image sources
      const imgSrc = img.getAttribute('src') || img.getAttribute('data-src');

      // Don't use srcset initially to reduce load
      // Browser will handle responsive images after initial load

      if (!imgSrc) return;

      // Add to queue
      imageQueue.add(() => {
        return new Promise((resolve) => {
          // If already loaded during queue wait, skip
          if (img.complete && img.naturalWidth > 0) {
            img.classList.add('loaded');
            resolve();
            return;
          }

          const loadTimeout = setTimeout(() => {
            console.warn('Image load timeout:', imgSrc);
            resolve();
          }, 5000); // 5 second timeout per image

          img.onload = () => {
            clearTimeout(loadTimeout);
            img.classList.add('loaded');
            resolve();
          };

          img.onerror = () => {
            clearTimeout(loadTimeout);
            console.error('Failed to load product image:', imgSrc);
            img.classList.add('error');
            resolve(); // Resolve anyway to continue queue
          };

          // If src not set, set it now
          if (!img.src || img.src !== imgSrc) {
            img.src = imgSrc;
          }
        });
      });
    });
  });
}

// Intersection Observer for visible products (load visible first)
let observer = null;

function setupIntersectionObserver() {
  if (!('IntersectionObserver' in window)) {
    // Fallback for browsers without IntersectionObserver
    forceLoadProductImages();
    return;
  }

  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const card = entry.target;
        const images = card.querySelectorAll('img');

        images.forEach(img => {
          if (!img.complete || img.naturalWidth === 0) {
            const imgSrc = img.getAttribute('src') || img.getAttribute('data-src');
            if (imgSrc && (!img.src || img.src !== imgSrc)) {
              img.setAttribute('loading', 'eager');
              img.src = imgSrc;
            }
          }
        });

        // Unobserve after loading
        observer.unobserve(card);
      }
    });
  }, {
    rootMargin: '200px', // Start loading 200px before entering viewport
    threshold: 0.01
  });

  // Observe all product cards
  document.querySelectorAll('.product-card').forEach(card => {
    observer.observe(card);
  });
}

// Run on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Wait a bit for DOM to settle
    setTimeout(() => {
      setupIntersectionObserver();
      forceLoadProductImages();
    }, 100);
  });
} else {
  setTimeout(() => {
    setupIntersectionObserver();
    forceLoadProductImages();
  }, 100);
}

// Run after additional delays for dynamic content
setTimeout(forceLoadProductImages, 1000);

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.forceLoadProductImages = forceLoadProductImages;
  window.imageLoadQueue = imageQueue;
  window.setupProductImageObserver = setupIntersectionObserver;
}
